import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { DialogTitle, Typography, useTheme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';

/**
 * Prototype dialog subtitle: body2 in `text2`, under the title (TMMAPP-0089), padding 4 24 0
 * (P:2011). The title above it closes its block at 4px (`MuiDialogTitle`, TMMAPP-0099).
 */
export function DialogSubtitle({ id, children }: { id?: string; children: ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Typography
      id={id}
      variant="body2"
      component="p"
      data-dialog-subtitle=""
      sx={{ px: 3, pt: 0.5, pb: 0, color: tokens.text2 }}
    >
      {children}
    </Typography>
  );
}

function isTitle(node: ReactNode): node is ReactElement<{ children?: ReactNode }> {
  return isValidElement(node) && node.type === DialogTitle;
}

function placeAfter(list: ReturnType<typeof Children.toArray>, at: number, subtitle: ReactNode) {
  if (subtitle == null || typeof subtitle === 'boolean') return list;
  const node = isValidElement(subtitle)
    ? cloneElement(subtitle, { key: subtitle.key ?? 'dialog-subtitle' })
    : subtitle;
  const next = [...list];
  next.splice(at + 1, 0, node);
  return next;
}

/** Places `subtitle` immediately after the dialog's `DialogTitle`, including one level inside a form or fragment. */
export function withDialogSubtitle(children: ReactNode, subtitle: ReactNode): ReactNode {
  const list = Children.toArray(children);
  const direct = list.findIndex(isTitle);
  if (direct !== -1) return placeAfter(list, direct, subtitle);
  return list.map((child) => {
    if (!isValidElement<{ children?: ReactNode }>(child) || child.props.children == null)
      return child;
    const inner = Children.toArray(child.props.children);
    const at = inner.findIndex(isTitle);
    if (at === -1) return child;
    return cloneElement(child, undefined, ...placeAfter(inner, at, subtitle));
  });
}
